import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { NotesService } from 'core/services/notes.service';

@Component({
  selector: 'app-notes',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './notes.html',
  styleUrl: './notes.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Notes implements OnInit, OnChanges, OnDestroy {
  private readonly notesService = inject(NotesService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  @Input({ required: true }) lessonId!: string;

  protected noteContent = '';
  protected saving = false;
  protected saveError: string | null = null;

  ngOnInit(): void {
    // Carregar anotação existente quando componente inicializa
    this.loadNoteForCurrentLesson();

    // Observar mudanças no conteúdo
    this.notesService.currentNote$
      .pipe(takeUntil(this.destroy$))
      .subscribe((content) => {
        this.noteContent = content;
        this.cdr.markForCheck();
      });

    // Observar estado de salvamento
    this.notesService.saving$
      .pipe(takeUntil(this.destroy$))
      .subscribe((saving) => {
        this.saving = saving;
        this.cdr.markForCheck();
      });

    // Observar erros de salvamento
    this.notesService.saveError$
      .pipe(takeUntil(this.destroy$))
      .subscribe((error) => {
        this.saveError = error;
        this.cdr.markForCheck();
      });
  }

  ngOnChanges(): void {
    // Recarregar anotação quando lessonId mudar
    if (this.lessonId) {
      this.loadNoteForCurrentLesson();
    }
  }

  /**
   * Carrega a anotação para a lição atual
   */
  private loadNoteForCurrentLesson(): void {
    this.notesService.loadNote(this.lessonId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Manipula mudanças no textarea
   * @param event Evento de input
   */
  protected onNoteChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    const content = target.value;
    this.noteContent = content;
    this.notesService.updateNote(content, this.lessonId);
  }

  /**
   * Limpa a anotação atual
   */
  protected onClearNote(): void {
    this.noteContent = '';
    this.notesService.deleteNote(this.lessonId);
  }
}
